/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-get, ember/no-runloop, ember/require-computed-property-dependencies, ember/require-tagless-components, getter-return, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import EmberObject, { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { later } from '@ember/runloop';
import { compare, isEmpty } from '@ember/utils';
import EventStatus from '@linc-technologies/linced-common/enums/bookings/event-status';
import { mapBy, sortBy } from '@linc-technologies/linced-common/utils/arrays';
import { hash } from '@linc-technologies/linced-common/utils/promise';
import {
  bookingTable,
  buildChipDataMap,
  teacherIdKey,
} from 'linced-app/utils/bookings';
import { getTablePrintCss } from 'linced-app/utils/table';

export default class BookingManager extends Component {
  @action
  setFilteredTeachers(value) {
    this.filteredTeachers = value;
  }

  // Services
  @service
  store;

  EventStatus = EventStatus;
  @tracked allocations = null;
  @tracked bookings = null;
  event = null;
  @tracked slots = null;
  @tracked
  chipDataMap = EmberObject.create({});
  @tracked filteredTeachers = [];
  @tracked
  openingDialog = false;
  @tracked manageBookingsDialogOpen = false;
  teacherId = '';

  @computed('event.teacherGroupMappings.[]')
  get teacherGroups() {
    // Sort teachers within their mapping by fullName,
    // And then sort each mapping by the first teacher's fullName
    return this.get('event.teacherGroupMappings')
      .reduce((teacherGroups, mapping) => {
        teacherGroups.push(sortBy(mapping.get('teachers'), 'fullName'));
        return teacherGroups;
      }, [])
      .sort((a, b) => compare(a[0]?.fullName, b[0]?.fullName));
  }

  get filteredGroups() {
    return this.get('event.teacherGroupMappings')
      .find((teacherGroupMapping) => {
        return teacherGroupMapping
          .get('teacherIds')
          .includes(this.filteredTeachers[0]?.id);
      })
      .get('groups');
  }

  get maxBookings() {
    return this.get('event.teacherGroupMappings')
      .find((teacherGroupMapping) => {
        return teacherGroupMapping
          .get('teacherIds')
          .includes(this.filteredTeachers[0]?.id);
      })
      .get('maxBookings');
  }

  @computed('event.status')
  get eventStatusLabel() {
    if (this.get('teacherId.length') === 0) {
      return '';
    }
    let eventStatus = this.get('event.status');
    switch (eventStatus) {
      case EventStatus.PUBLISHED:
      case EventStatus.CLOSED:
        return '(Open to Staff only)';
      case EventStatus.OPEN:
        return '(Open to Caregivers)';
    }
  }

  @computed('event.status')
  get editable() {
    return ![EventStatus.COMPLETED, EventStatus.CANCELLED].includes(
      this.get('event.status'),
    );
  }

  @computed('filteredTeachers.@each.id')
  get teacherFilter() {
    return teacherIdKey(mapBy(this.filteredTeachers, 'id'));
  }

  @computed('allocations.@each.booked', 'teacherFilter')
  get printButtonDisabled() {
    if (isEmpty(this.allocations)) {
      return true;
    }
    let data = this.chipDataMap.get(this.teacherFilter);
    return !Object.values(data).some((dataSet) => {
      return dataSet.some((data) => {
        return data.get('allocation.booked');
      });
    });
  }

  @action
  openDialog() {
    this.openingDialog = true;
    let eventId = this.get('event.id');
    let teacherId = this.teacherId;
    hash({
      allocations: this.store.query('bookings/allocation', {
        eventId,
        teacherId,
      }),
      bookings: this.store.query('bookings/booking', { eventId }),
      slots: this.store.query('bookings/slot', { eventId }),
    })
      .then((data) => {
        let filteredTeachers = this.teacherGroups[0];
        if (this.teacherId) {
          let teacherGroupMappings = this.get('event.teacherGroupMappings');
          let mapping = teacherGroupMappings.find((mapping) => {
            return mapping.teacherIds.includes(this.teacherId);
          });
          filteredTeachers = mapping.teachers;
        }
        this.allocations = data.allocations;
        this.bookings = data.bookings;
        this.slots = data.slots;
        this.filteredTeachers = filteredTeachers;
        this.manageBookingsDialogOpen = true;
        this.chipDataMap = buildChipDataMap(
          this.allocations,
          this.slots,
          this.bookings,
        );
      })
      .finally(() => {
        this.openingDialog = false;
      });
  }

  @action
  print() {
    let printer = window.open(
      'about:blank',
      '_blank',
      'menubar=yes,location=no,resizable=yes,scrollbars=yes,status=no,height=700,width=1024',
    );

    let content = getTablePrintCss(false);

    content += `<h1>${this.event.get('name')}</h1>`;

    let teacherNames = mapBy(this.filteredTeachers, 'fullName').join(', ');
    content += `<h2>${teacherNames}</h2>`;

    let data = this.chipDataMap.get(this.teacherFilter);

    content += bookingTable(data, this.event);

    printer.document.body.innerHTML = content;

    later(
      this,
      (printer) => {
        printer.print();
        printer.close();
      },
      printer,
      500,
    );
  }
}
